import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { INSIGHT_V2_PLACEHOLDER_IMAGE } from '../content';

const Figure = styled.figure<{ $width: 'body' | 'wide' | 'full'; $rounded: number }>`
  width: 100%;
  margin: 0;
  display: grid;
  gap: 0.85rem;
  justify-self: center;
  max-width: ${({ $width }) =>
    $width === 'body' ? '46rem' : $width === 'wide' ? '58rem' : '100%'};
  border-radius: ${({ $rounded }) => `${$rounded}px`};
`;

const Placeholder = styled.div<{ $minHeight: number; $surface: string; $accent: string }>`
  min-height: ${({ $minHeight }) => `${$minHeight}px`};
  border-radius: inherit;
  display: grid;
  place-items: center;
  padding: 1.4rem;
  text-align: center;
  background:
    radial-gradient(circle at top, rgba(255, 255, 255, 0.18), transparent 44%),
    ${({ $surface }) => $surface};
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(246, 240, 233, 0.86);
  box-shadow: 0 22px 48px rgba(8, 14, 24, 0.16);

  strong {
    font-size: 0.95rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ $accent }) => $accent};
  }

  span {
    display: block;
    margin-top: 0.45rem;
    font-size: 0.9rem;
    line-height: 1.6;
    color: rgba(246, 240, 233, 0.78);
  }
`;

const ImageShell = styled.div<{ $minHeight: number; $fit: 'cover' | 'contain' }>`
  min-height: ${({ $minHeight }) => `${$minHeight}px`};
  border-radius: inherit;
  overflow: hidden;
  border: 1px solid rgba(15, 23, 38, 0.08);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.74), rgba(239, 236, 240, 0.92));
  box-shadow: 0 18px 42px rgba(8, 14, 24, 0.14);
  display: grid;
  place-items: center;

  img {
    display: block;
    width: 100%;
    height: ${({ $fit }) => ($fit === 'contain' ? 'auto' : '100%')};
    min-height: ${({ $fit, $minHeight }) => ($fit === 'cover' ? `${$minHeight}px` : '0')};
    max-height: ${({ $fit }) => ($fit === 'contain' ? '80vh' : 'none')};
    object-fit: ${({ $fit }) => $fit};
    object-position: center center;
  }
`;

const Caption = styled.figcaption`
  max-width: 42rem;
  color: rgba(20, 34, 53, 0.66);
  font-size: 0.92rem;
  line-height: 1.6;
`;

type InsightImageProps = {
  src?: string | null;
  alt?: string;
  caption?: string;
  width?: 'body' | 'wide' | 'full';
  fit?: 'cover' | 'contain';
  minHeight?: number;
  rounded?: number;
  accent?: string;
  surface?: string;
  fallbackLabel?: string;
};

export default function InsightImage({
  src,
  alt,
  caption,
  width = 'wide',
  fit = 'cover',
  minHeight = 320,
  rounded = 28,
  accent = '#e0a15e',
  surface = 'linear-gradient(145deg, rgba(13, 20, 33, 0.98), rgba(27, 38, 58, 0.94))',
  fallbackLabel = 'Image preview',
}: InsightImageProps) {
  const [broken, setBroken] = useState(false);
  const resolvedSrc = useMemo(
    () => (typeof src === 'string' && src.trim().length > 0 ? src.trim() : INSIGHT_V2_PLACEHOLDER_IMAGE),
    [src],
  );
  const showImage = !broken;

  const resolvedAlt = useMemo(
    () => alt?.trim() || caption?.trim() || fallbackLabel,
    [alt, caption, fallbackLabel],
  );

  return (
    <Figure $width={width} $rounded={rounded}>
      {showImage ? (
        <ImageShell $minHeight={minHeight} $fit={fit}>
          <img src={resolvedSrc} alt={resolvedAlt} onError={() => setBroken(true)} />
        </ImageShell>
      ) : (
        <Placeholder $minHeight={minHeight} $surface={surface} $accent={accent}>
          <div>
            <strong>{fallbackLabel}</strong>
            <span>Missing or unavailable media renders as a styled placeholder without breaking the article.</span>
          </div>
        </Placeholder>
      )}
      {caption ? <Caption>{caption}</Caption> : null}
    </Figure>
  );
}
